import { Button, Modal } from 'app/shared/components';
import { css } from 'emotion';
import React, { ReactNode } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';

export interface ModalConfirmOptions {
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}

/**
 * A simple confirm modal w/o animation.
 *
 * @example
 *
 * modalConfirm(
 * 	(
 * 		<div>
 * 			Want to eat SurveyCake ?
 * 		</div>
 * 	),
 * 	{
 * 		confirmText: 'Yes',
 * 		cancelText: 'No',
 * 		onConfirm: () => { },
 *    onCancel: () => { }
 * 	}
 * );
 */
export function modalConfirm(
  content: ReactNode,
  options?: ModalConfirmOptions
) {
  const root = document.createElement('div');
  document.body.appendChild(root);
  const confirmText = options && options.confirmText ? options.confirmText : '';
  const cancelText = options && options.cancelText ? options.cancelText : '';

  const classes = {
    modal: css(`
			display: flex;
			flex-direction: column;
			width: 450px;
			max-width: 94vw;
			padding: 20px;
			box-sizing: border-box;
		`),
    content: css(`
			font-size: 20px;
		`),
    footer: css(`
			display: flex;
			justify-content: flex-end;
			margin-top: 20px;
		`),
    action: css(`
			margin-left: 20px;
			min-width: 100px;
		`)
  };

  function close() {
    unmountComponentAtNode(root);
    document.body.removeChild(root);
  }

  function onConfirm() {
    close();

    if (options && options.onConfirm) {
      options.onConfirm();
    }
  }

  function onCancel() {
    close();

    if (options && options.onCancel) {
      options.onCancel();
    }
  }

  render(
    <Modal
      className={classes.modal}
      onOpen={() => {
        document.body.style.overflowY = 'hidden';
      }}
      onClose={() => {
        document.body.style.overflowY = '';
      }}
    >
      <div className={classes.content}>{content}</div>
      {(cancelText || confirmText) && (
        <div className={classes.footer}>
          {cancelText && (
            <Button className={classes.action} onClick={onCancel}>
              {cancelText}
            </Button>
          )}
          {confirmText && (
            <Button
              className={classes.action}
              color="primary"
              onClick={onConfirm}
            >
              {confirmText}
            </Button>
          )}
        </div>
      )}
    </Modal>,
    root
  );
}
